import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { CheckCircle, XCircle, Clock, IndianRupee, CreditCard } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WithdrawalRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState({ open: false, request: null, action: '' });
  const [adminComment, setAdminComment] = useState('');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/withdrawal/requests`);
      setRequests(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load withdrawal requests",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/withdrawal/requests/${actionDialog.request.id}`,
        {
          status: action,
          admin_comment: adminComment || null
        }
      );
      toast({
        title: "Success",
        description: `Withdrawal request ${action}`
      });
      setActionDialog({ open: false, request: null, action: '' });
      setAdminComment('');
      fetchRequests();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || `Failed to ${action} request`,
        variant: "destructive"
      });
    }
  };

  const openActionDialog = (request, action) => {
    setActionDialog({ open: true, request, action });
    setAdminComment('');
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const approvedRequests = requests.filter(r => r.status === 'approved');
  const deniedRequests = requests.filter(r => r.status === 'denied');

  const totalPendingAmount = pendingRequests.reduce((sum, r) => sum + r.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-orange-100 text-orange-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'denied':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Denied</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Withdrawal Requests</CardTitle>
          <CardDescription>Manage user withdrawal requests</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Pending</p>
                  <p className="text-2xl font-bold text-orange-600">{pendingRequests.length}</p>
                </div>
                <Clock className="w-8 h-8 text-orange-300" />
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Approved</p>
                  <p className="text-2xl font-bold text-green-600">{approvedRequests.length}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-300" />
              </div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Denied</p>
                  <p className="text-2xl font-bold text-red-600">{deniedRequests.length}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-300" />
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Pending Amount</p>
                  <p className="text-2xl font-bold text-blue-600 flex items-center">
                    <IndianRupee className="w-5 h-5" />
                    {totalPendingAmount.toFixed(2)}
                  </p>
                </div>
                <CreditCard className="w-8 h-8 text-blue-300" />
              </div>
            </div>
          </div>

          {/* Requests Table */}
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">User</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Contact</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Referral Code</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Amount</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">UPI ID</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Request Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-slate-500">
                        No withdrawal requests found
                      </td>
                    </tr>
                  ) : (
                    requests.map((request) => (
                      <tr key={request.id} className="border-t hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium">{request.user_name || 'Unknown'}</p>
                            <p className="text-xs text-slate-500">{request.user_email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">{request.user_mobile || 'N/A'}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="font-mono text-xs">
                            {request.user_referral_code || 'N/A'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-green-600">
                          ₹{request.amount.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono">{request.upi_id || 'N/A'}</td>
                        <td className="px-4 py-3">{getStatusBadge(request.status)}</td>
                        <td className="px-4 py-3 text-sm">
                          {new Date(request.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          {request.status === 'pending' ? (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => openActionDialog(request, 'approved')}
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => openActionDialog(request, 'denied')}
                              >
                                Deny
                              </Button>
                            </div>
                          ) : (
                            <div className="text-xs text-slate-500">
                              {request.updated_at ? new Date(request.updated_at).toLocaleDateString() : '-'}
                              {request.admin_comment && (
                                <p className="mt-1 italic">"{request.admin_comment}"</p>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ open, request: null, action: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.action === 'approved' ? 'Approve' : 'Deny'} Withdrawal Request
            </DialogTitle>
            <DialogDescription>
              {actionDialog.action === 'approved' 
                ? 'This will deduct the amount from user\'s earnings and mark the request as approved.'
                : 'The user will be notified that their withdrawal request was denied.'}
            </DialogDescription>
          </DialogHeader>
          {actionDialog.request && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <p className="text-sm"><strong>User:</strong> {actionDialog.request.user_name}</p>
                <p className="text-sm"><strong>Amount:</strong> ₹{actionDialog.request.amount}</p>
                <p className="text-sm"><strong>UPI ID:</strong> {actionDialog.request.upi_id || 'N/A'}</p>
              </div>
              <div>
                <Label>Admin Comment (Optional)</Label>
                <Textarea
                  placeholder="Add a comment for this action..."
                  value={adminComment}
                  onChange={(e) => setAdminComment(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setActionDialog({ open: false, request: null, action: '' })}
                >
                  Cancel
                </Button>
                <Button
                  className={actionDialog.action === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                  onClick={() => handleAction(actionDialog.action)}
                >
                  Confirm {actionDialog.action === 'approved' ? 'Approval' : 'Denial'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WithdrawalRequests;
